import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

export type SiteTheme = "light" | "dark"

const STORAGE_KEY = "tessel-site-theme"

/* ──────────────────────────────────────────────────────────────────────────
   Palette key registry.

   The single source of truth for the 8 colors that drive the "grimoire"
   identity. Adding a color is a 3-step task:

     1. Add the key here.
     2. Add the matching fallback hex in FALLBACK_PALETTE below.
     3. Add the matching CSS variable in apps/site/src/globals.css under
        both `:root` (light) and `.dark` (dark).

   The CSS variable name is derived from the key via cssVarName():
   `--site-` + lowercased + underscores-to-dashes (e.g. GOLD_LIGHT becomes
   `--site-gold-light`). Keeping the mapping deterministic means the JS
   type-system enforces the JS-side contract — you cannot read a palette
   key that doesn't exist — while the CSS side is the only manual sync.
   ────────────────────────────────────────────────────────────────────────── */

const PALETTE_KEYS = [
  "DEEP_INDIGO",
  "CONTENT_BG",
  "CONTENT_ALT",
  "GOLD",
  "GOLD_LIGHT",
  "RUNE_BLUE",
  "EMERALD",
  "PARCHMENT",
] as const

export type PaletteKey = (typeof PALETTE_KEYS)[number]
export type Palette = Record<PaletteKey, string>

/** Map a palette key to its CSS custom property name. */
function cssVarName(key: PaletteKey): string {
  return `--site-${key.toLowerCase().replace(/_/g, "-")}`
}

/**
 * Dark values double as the SSR / first-paint fallback when the document is
 * not yet available to read computed styles from. Light values live in
 * globals.css under `:root`; consumers reach them through the active CSS
 * variables, never directly from JS.
 */
const FALLBACK_PALETTE: Palette = {
  DEEP_INDIGO: "#0f0d1a",
  CONTENT_BG: "#1f1b2e",
  CONTENT_ALT: "#252240",
  GOLD: "#d4b076",
  GOLD_LIGHT: "#e8d4a8",
  RUNE_BLUE: "#a8b4ff",
  EMERALD: "#7edcb4",
  PARCHMENT: "#ece6f5",
}

function readPaletteFromCSS(): Palette {
  if (typeof window === "undefined") {
    return FALLBACK_PALETTE
  }
  const style = getComputedStyle(document.documentElement)
  const result = { ...FALLBACK_PALETTE }
  for (const key of PALETTE_KEYS) {
    const value = style.getPropertyValue(cssVarName(key)).trim()
    if (value.length > 0) {
      result[key] = value
    }
  }
  return result
}

/* ──────────────────────────────────────────────────────────────────────────
   Theme provider.

   Owns three pieces of state:
     - `theme`: the active "light" | "dark" preference.
     - `palette`: the resolved 8-color object, derived from CSS variables
       on every theme change. Stored in React state and provided to
       consumers via context — consumers never call getComputedStyle
       themselves, which keeps the hot path off the DOM.
     - The `.dark` class on <html>, applied synchronously in setTheme so
       getComputedStyle reads in the same tick already see new values.

   The matchMedia listener honors live OS theme changes only when the user
   has not explicitly chosen a theme yet (no value stored under
   STORAGE_KEY). Once they toggle, we respect that choice across sessions.
   ────────────────────────────────────────────────────────────────────────── */

/**
 * Resolve the initial theme: stored preference wins, otherwise honor the
 * operating system via `prefers-color-scheme`. Falls back to dark when no
 * signal is available (SSR or no media-query support).
 */
function getInitialTheme(): SiteTheme {
  if (typeof window === "undefined") {
    return "dark"
  }
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === "light" || stored === "dark") {
    return stored
  }
  if (window.matchMedia("(prefers-color-scheme: light)").matches) {
    return "light"
  }
  return "dark"
}

/**
 * Sync the active theme to <html>. The class is read by the CSS variants
 * in @tessel/ui (`@custom-variant dark (&:is(.dark *))`) and by the site's
 * own `.dark { --site-* }` block. Living on the document root means
 * getComputedStyle(document.documentElement) resolves against the right
 * variable set when the provider recomputes the palette.
 */
function applyThemeClass(theme: SiteTheme) {
  if (typeof document === "undefined") {
    return
  }
  document.documentElement.classList.toggle("dark", theme === "dark")
}

/**
 * Persist the user's explicit theme choice. Lives outside the provider so
 * its identity is stable across renders and React hook callers can omit
 * it from their dependency arrays without lying.
 */
function persistTheme(theme: SiteTheme) {
  if (typeof window === "undefined") {
    return
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // localStorage can throw in private mode / quota errors — ignore.
  }
}

type ThemeContextValue = {
  theme: SiteTheme
  palette: Palette
  setTheme: (theme: SiteTheme) => void
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

type ThemeProviderProps = {
  children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<SiteTheme>(getInitialTheme)
  const [palette, setPalette] = useState<Palette>(FALLBACK_PALETTE)

  // Re-read the palette whenever the active theme changes. Applied as an
  // effect (rather than synchronously in setTheme) so the first mount also
  // picks up the variables resolved by the inline bootstrap in index.html.
  useEffect(() => {
    applyThemeClass(theme)
    setPalette(readPaletteFromCSS())
  }, [theme])

  // Live OS theme changes: only honor them while the user has no explicit
  // preference. Once they toggle, the stored value wins across sessions.
  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }
    const mql = window.matchMedia("(prefers-color-scheme: dark)")
    const onChange = (event: MediaQueryListEvent) => {
      try {
        if (window.localStorage.getItem(STORAGE_KEY) === null) {
          setThemeState(event.matches ? "dark" : "light")
        }
      } catch {
        // Ignore storage failures — fall back to honoring the OS.
        setThemeState(event.matches ? "dark" : "light")
      }
    }
    mql.addEventListener("change", onChange)
    return () => {
      mql.removeEventListener("change", onChange)
    }
  }, [])

  const setTheme = useCallback((next: SiteTheme) => {
    // Apply class FIRST so getComputedStyle in the upcoming render already
    // sees the new variable set, and consumers receive the right palette
    // in a single batched re-render.
    applyThemeClass(next)
    persistTheme(next)
    setPalette(readPaletteFromCSS())
    setThemeState(next)
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next: SiteTheme = prev === "dark" ? "light" : "dark"
      applyThemeClass(next)
      persistTheme(next)
      setPalette(readPaletteFromCSS())
      return next
    })
  }, [])

  const value = useMemo(
    () => ({ theme, palette, setTheme, toggleTheme }),
    [theme, palette, setTheme, toggleTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

function useThemeContext(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    throw new Error("useTheme / usePalette must be used within a ThemeProvider")
  }
  return ctx
}

/** Access the active theme and the toggle/setter handles. */
export function useTheme() {
  const { theme, setTheme, toggleTheme } = useThemeContext()
  return { theme, setTheme, toggleTheme }
}

/**
 * Reactive palette accessor. Returns the currently active "grimoire"
 * palette as 6-digit hex strings — the same shape the legacy module-level
 * constants had, so JSX patterns like `${GOLD}10` (hex+alpha) keep working
 * verbatim.
 *
 * Values come from React state seeded by getComputedStyle on theme change,
 * so this hook is a plain context read at the hot path — no per-render
 * DOM lookups.
 */
export function usePalette(): Palette {
  return useThemeContext().palette
}
